import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Checkbox,
  Divider,
  FormHelperText,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';
import { stopWheelNudge } from '../utils/numberInput';
import DuplicateSessionConfirmDialog from './DuplicateSessionConfirmDialog';
import { getDuplicateSessionError, dropSkipped, DuplicateSessionInfo, DuplicateEntry } from '../utils/duplicateSession';

interface SessionFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  session?: any; // If provided, it's edit mode
  studioId?: string; // Pre-fill studio info
  defaultStartDate?: string;
}

const SessionFormDialog: React.FC<SessionFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  session,
  studioId,
  defaultStartDate,
}) => {
  const isEditMode = Boolean(session);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Duplicate-session confirmation: the built payload is parked here when the
  // backend rejects it with DUPLICATE_SESSION, then re-sent with allowDuplicate.
  const [pendingSessions, setPendingSessions] = useState<any[] | null>(null);
  const [dupInfo, setDupInfo] = useState<DuplicateSessionInfo | null>(null);
  const [dupLoading, setDupLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    class: '',
    date: '',
    slots: '',
    isLive: true,
    onlyForFemales: false,
    isTrialEnabled: false,
    trialPrice: '',
    // Per-session instructor override (edit mode only). Empty = follow the class.
    instructor: '',
    // Per-session price (edit mode only). Editing the class price never changes
    // an existing session — only this field does.
    price: '',
  });

  const [selectedDays, setSelectedDays] = useState<Record<string, boolean>>({});
  const [dayTimes, setDayTimes] = useState<Record<string, string>>({});
  const [daySlots, setDaySlots] = useState<Record<string, string>>({});

  const [startDate, setStartDate] = useState('');

  const [classes, setClasses] = useState<any[]>([]);
  const [instructors, setInstructors] = useState<any[]>([]);
  const [studio, setStudio] = useState<any>(null);
  // Opt-in, deliberately false to start — mirrors the instructor delete/
  // reassign flow's notifyMembers checkbox: telling members who already
  // booked is the studio's call, not a side effect of a schedule edit.
  const [notifyInstructorChange, setNotifyInstructorChange] = useState(false);

  const daysOfWeek = [
    { key: 'monday', label: 'Monday', dayNum: 1 },
    { key: 'tuesday', label: 'Tuesday', dayNum: 2 },
    { key: 'wednesday', label: 'Wednesday', dayNum: 3 },
    { key: 'thursday', label: 'Thursday', dayNum: 4 },
    { key: 'friday', label: 'Friday', dayNum: 5 },
    { key: 'saturday', label: 'Saturday', dayNum: 6 },
    { key: 'sunday', label: 'Sunday', dayNum: 0 },
  ];

  const getDaysFromStartDate = (startDateStr: string): Array<{ key: string; dateKey: string; label: string; dayNum: number; date: Date }> => {
    if (!startDateStr) {
      return daysOfWeek.map(d => ({ ...d, dateKey: '', date: new Date() }));
    }
    
    const startDate = new Date(startDateStr + 'T00:00:00');
    
    const orderedDays: Array<{ key: string; dateKey: string; label: string; dayNum: number; date: Date }> = [];
    
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const currentDay = currentDate.getDay();
      
      const dayInfo = daysOfWeek.find(d => d.dayNum === currentDay);
      if (dayInfo) {
        const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
        orderedDays.push({ ...dayInfo, dateKey, date: new Date(currentDate) });
      }
    }
    
    return orderedDays;
  };

  const getStartDateDayName = (startDateStr: string): string => {
    if (!startDateStr) return '';
    const date = new Date(startDateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const fetchStudioData = useCallback(async () => {
    if (!studioId) return;
    try {
      const response = await apiService.getStudioById(studioId);
      setStudio(response.studio);
    } catch (err: any) {
      console.error('Failed to fetch studio data:', err);
    }
  }, [studioId]);

  const fetchDropdownData = useCallback(async () => {
    try {
      if (!studioId && !session?.studio) {
        console.error('No studio ID available');
        return;
      }
      const currentStudioId = studioId || session?.studio?._id || session?.studio;
      const classesRes = await apiService.getStudioClasses(currentStudioId);
      setClasses(classesRes.classes || []);

      // Instructors are only needed for the edit-mode per-session override.
      // Skipped on create so the create flow makes no extra request.
      if (session) {
        try {
          const instructorsRes = await apiService.getInstructorsByStudio(currentStudioId);
          setInstructors(instructorsRes.instructors || []);
        } catch (instErr: any) {
          console.error('Failed to fetch instructors:', instErr);
          setInstructors([]);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch classes:', err);
    }
  }, [studioId, session]);

  const populateForm = useCallback(() => {
    if (!session) return;
    
    let localDateTime = '';
    if (session.date) {
      const utcDate = new Date(session.date);
      // Convert UTC → IST for display so admin edits in IST
      const istDate = new Date(utcDate.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      const year = istDate.getFullYear();
      const month = String(istDate.getMonth() + 1).padStart(2, '0');
      const day = String(istDate.getDate()).padStart(2, '0');
      const hours = String(istDate.getHours()).padStart(2, '0');
      const minutes = String(istDate.getMinutes()).padStart(2, '0');
      localDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    
    setFormData({
      class: session.class?._id || session.class || '',
      date: localDateTime,
      slots: session.slots?.toString() || '',
      isLive: session.isLive !== undefined ? session.isLive : true,
      onlyForFemales: session.onlyForFemales || false,
      isTrialEnabled: session.isTrialEnabled || false,
      trialPrice: session.trialPrice?.toString() || '',
      // Prefill with whoever is actually teaching THIS session, which may already
      // differ from the class default if it was overridden before.
      instructor: session.instructor?._id || session.instructor || '',
      // THIS session's own price, which may differ from the class price.
      price: session.price !== undefined && session.price !== null ? String(session.price) : '',
    });
    setNotifyInstructorChange(false);
  }, [session]);

  const resetForm = useCallback(() => {
    setFormData({
      class: '',
      date: '',
      slots: '',
      isLive: true,
      onlyForFemales: false,
      isTrialEnabled: false,
      trialPrice: '',
      instructor: '',
      price: '',
    });
    setSelectedDays({});
    setDayTimes({});
    setDaySlots({});
    setStartDate(defaultStartDate || '');
  }, [defaultStartDate]);

  useEffect(() => {
    if (open) {
      fetchDropdownData();
      if (studioId) {
        fetchStudioData();
      }
      if (session) {
        populateForm();
      } else {
        resetForm();
      }
    }
  }, [open, session, studioId, fetchDropdownData, fetchStudioData, populateForm, resetForm]);

  const handleChange = (field: string, value: any) => {
    const previousSlots = formData.slots;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Changing the class: pre-fill the session fields the form owns from the
    // NEW class so the operator sees them and confirms (or edits) before saving.
    // Whatever is sent wins server-side; the rest (name, duration, …) follows
    // the new class.
    if (field === 'class') {
      const nextClass = classes.find(c => c._id === value);
      setFormData(prev => ({
        ...prev,
        instructor: nextClass?.instructor?._id || nextClass?.instructor || '',
        price:
          nextClass?.price !== undefined && nextClass?.price !== null ? String(nextClass.price) : prev.price,
      }));
    }

    if (field === 'slots' && value) {
      Object.keys(selectedDays).forEach(dateKey => {
        if (selectedDays[dateKey]) {
          const currentDaySlots = daySlots[dateKey];
          if (!currentDaySlots || currentDaySlots === previousSlots) {
            setDaySlots(prev => ({ ...prev, [dateKey]: value }));
          }
        }
      });
    }
  };

  const handleDayToggle = (dateKey: string) => {
    const isSelecting = !selectedDays[dateKey];
    setSelectedDays(prev => ({
      ...prev,
      [dateKey]: isSelecting,
    }));
    
    if (isSelecting) {
      setDaySlots(prev => ({
        ...prev,
        [dateKey]: prev[dateKey] ?? formData.slots ?? '',
      }));
    } else {
      setDayTimes(prev => {
        const newTimes = { ...prev };
        delete newTimes[dateKey];
        return newTimes;
      });
      setDaySlots(prev => {
        const newSlots = { ...prev };
        delete newSlots[dateKey];
        return newSlots;
      });
    }
  };

  const handleDayTimeChange = (dateKey: string, time: string) => {
    setDayTimes(prev => ({
      ...prev,
      [dateKey]: time,
    }));
  };

  const handleDaySlotsChange = (dateKey: string, slots: string) => {
    setDaySlots(prev => ({
      ...prev,
      [dateKey]: slots,
    }));
  };


  const formatDateString = (date: Date, time: string): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T${time}:00.000+05:30`;
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isEditMode) {
        const [datePart, timePart] = formData.date.split('T');
        if (!datePart || !timePart) {
          throw new Error('Invalid date format. Expected YYYY-MM-DDTHH:mm');
        }
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);
        
        if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hours) || isNaN(minutes)) {
          throw new Error('Invalid date/time values');
        }
        
        const istDateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00.000+05:30`;
      
      const payload: any = {
        class: formData.class,
        date: istDateString,
        slots: parseInt(formData.slots),
        isLive: formData.isLive,
        onlyForFemales: formData.onlyForFemales,
        isTrialEnabled: formData.isTrialEnabled,
        trialPrice: formData.isTrialEnabled ? parseFloat(formData.trialPrice) || 0 : 0,
      };

      // Per-session price. Blank = leave the session's price as it is.
      if (formData.price.trim() !== '') {
        const price = Number(formData.price);
        if (!Number.isFinite(price) || price < 0) {
          throw new Error('Price must be 0 or more');
        }
        payload.price = price;
      }

      // Only send the override when one is actually chosen. Omitting it lets the
      // backend keep its existing behaviour (derive from the class on class change,
      // otherwise leave the session's instructor untouched).
      if (formData.instructor) {
        payload.instructor = formData.instructor;
      }

      // Sent regardless of whether this is a real change or has bookings —
      // the backend already no-ops (and never emails anyone) in both cases.
      const originalInstructorId = session.instructor?._id || session.instructor || '';
      if (notifyInstructorChange && formData.instructor !== originalInstructorId) {
        payload.notifyMembers = true;
      }

        // Pinned to the session's own location — under "All locations" the
        // switcher sends __all__, which the server refuses for writes.
        await apiService.updateSession(session._id, payload, studioIdOf(session.studio) || studioId);
        onSuccess();
        onClose();
        return;
      }

        if (!studioId) {
          throw new Error('Studio ID is required to create a session');
        }

      const selectedDaysList = Object.keys(selectedDays).filter(dateKey => selectedDays[dateKey]);
      
      if (selectedDaysList.length === 0) {
        throw new Error('Please select at least one day');
      }

      if (!startDate) {
        throw new Error('Please select a start date');
      }

      const daysFromStart = getDaysFromStartDate(startDate);
      const missingTimes = selectedDaysList.filter(dateKey => !dayTimes[dateKey]);
      if (missingTimes.length > 0) {
        const missingDays = missingTimes.map(dateKey => {
          const dayInfo = daysFromStart.find(d => d.dateKey === dateKey);
          return dayInfo ? dayInfo.label : dateKey;
        });
        throw new Error(`Please provide time for: ${missingDays.join(', ')}`);
      }

      const sessions = selectedDaysList.map(dateKey => {
        const dayInfo = daysFromStart.find(d => d.dateKey === dateKey);
        if (!dayInfo) {
          throw new Error(`Could not find date for ${dateKey}`);
        }
        const dateString = formatDateString(dayInfo.date, dayTimes[dateKey]);
        const slotsForDay = daySlots[dateKey] || formData.slots;

        return {
          class: formData.class,
          date: dateString,
          slots: parseInt(slotsForDay),
          isLive: formData.isLive,
          onlyForFemales: formData.onlyForFemales,
          isTrialEnabled: formData.isTrialEnabled,
          trialPrice: formData.isTrialEnabled ? parseFloat(formData.trialPrice) || 0 : 0,
        };
      });

      try {
        await apiService.createMultipleSessions(studioId, { sessions });
      } catch (err: any) {
        const dup = getDuplicateSessionError(err);
        if (dup) {
          // Hold the payload and let the operator confirm — the retry re-sends
          // it verbatim with allowDuplicate: true.
          setPendingSessions(sessions);
          setDupInfo(dup);
          return;
        }
        throw err;
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDuplicate = async (skip: DuplicateEntry[]) => {
    if (!pendingSessions || !studioId) return;
    const sessions = dropSkipped(pendingSessions, skip);
    if (sessions.length === 0) {
      // Operator unchecked every collision and there is nothing else to create.
      setDupInfo(null);
      setPendingSessions(null);
      return;
    }
    setDupLoading(true);
    setError(null);
    try {
      await apiService.createMultipleSessions(studioId, {
        sessions,
        allowDuplicate: true,
      });
      setDupInfo(null);
      setPendingSessions(null);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Operation failed');
      setDupInfo(null);
      setPendingSessions(null);
    } finally {
      setDupLoading(false);
    }
  };

  const selectedClass = classes.find(c => c._id === formData.class);

  // The class's own instructor — the default this session inherited at creation.
  const classInstructorId = selectedClass?.instructor?._id || selectedClass?.instructor || '';
  // Pick list: active instructors, plus the currently-assigned one even if it has
  // since been disabled, so editing an old session never silently drops its coach.
  const selectableInstructors = instructors.filter(
    (i: any) => !i.isDisabled || i._id === formData.instructor,
  );
  const isInstructorOverridden =
    Boolean(formData.instructor) && formData.instructor !== classInstructorId;
  const originalInstructorId = session?.instructor?._id || session?.instructor || '';
  const instructorIsChanging = isEditMode && Boolean(formData.instructor) && formData.instructor !== originalInstructorId;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{isEditMode ? 'Edit Session' : 'Create Session'}</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        {studio && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Session will be created for: <strong>{studio.studioName}</strong>
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2}>
          {/* Class Selection */}
          <FormControl fullWidth size="small">
            <InputLabel>Class *</InputLabel>
            <Select
              value={formData.class}
              onChange={(e) => handleChange('class', e.target.value)}
              label="Class *"
            >
              {classes.map((cls) => (
                <MenuItem key={cls._id} value={cls._id}>
                  {cls.className} - ₹{cls.price} ({cls.duration} mins)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedClass && (
            <Alert severity="info">
              <Typography variant="body2">
                <strong>{selectedClass.className}</strong><br />
                {isEditMode ? 'Class instructor' : 'Instructor'}: {selectedClass.instructor?.name || 'N/A'}<br />
                Duration: {selectedClass.duration} mins | Price: ₹{selectedClass.price}<br />
                Type: {selectedClass.classType?.title || 'N/A'}
              </Typography>
            </Alert>
          )}

          {isEditMode ? (
            <>
              {/* Per-session instructor override — reassign a single session (a
                  cover/sub) without touching the class or any sibling session. */}
              <FormControl fullWidth size="small">
                <InputLabel>Instructor</InputLabel>
                <Select
                  value={
                    selectableInstructors.some((i: any) => i._id === formData.instructor)
                      ? formData.instructor
                      : ''
                  }
                  onChange={(e) => handleChange('instructor', e.target.value)}
                  label="Instructor"
                >
                  {selectableInstructors.map((inst: any) => (
                    <MenuItem key={inst._id} value={inst._id}>
                      {inst.name}
                      {inst._id === classInstructorId ? ' (class default)' : ''}
                      {inst.isDisabled ? ' — disabled' : ''}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {instructors.length === 0
                    ? 'Loading instructors…'
                    : isInstructorOverridden
                      ? `Overrides the class instructor${selectedClass?.instructor?.name ? ` (${selectedClass.instructor.name})` : ''} for this session only.`
                      : 'Applies to this session only — the class and other sessions are unchanged.'}
                </FormHelperText>
              </FormControl>

              {/* Reminder mirrors the delete/reassign flow's — this session
                  may already have members booked into it, and a swap here
                  never touches that other path (nobody is being removed). */}
              {instructorIsChanging && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={notifyInstructorChange}
                      onChange={(e) => setNotifyInstructorChange(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      If members have already booked this session, tell them the instructor changed
                    </Typography>
                  }
                />
              )}

              <TextField
                label="Date & Time * (IST - Indian Standard Time)"
                type="datetime-local"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                helperText="Enter time in 24-hour format (e.g., 18:30 for 6:30 PM IST). The time you enter is treated as IST."
                inputProps={{ step: 60 }}
              />
              <TextField
                label="Slots *"
                type="number"
                value={formData.slots}
                onChange={(e) => handleChange('slots', e.target.value)}
                onWheel={stopWheelNudge}
                fullWidth
                size="small"
                helperText="Total number of available spots for this session"
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Price (₹)"
                type="number"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value)}
                onWheel={stopWheelNudge}
                fullWidth
                size="small"
                helperText={
                  selectedClass && formData.price !== '' && Number(formData.price) !== Number(selectedClass.price)
                    ? `This session only — the class price is ₹${selectedClass.price}.`
                    : 'This session only. 0 = free. Changing the class price does not change this.'
                }
                inputProps={{ min: 0 }}
              />
            </>
          ) : (
            <>
              <TextField
                label="Start Date *"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                helperText={
                  startDate 
                    ? `Start Date: ${getStartDateDayName(startDate)}, ${new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                    : 'Select the start date for recurring sessions'
                }
                inputProps={{
                  min: new Date().toISOString().split('T')[0],
                }}
              />

          <TextField
            label="Slots *"
            type="number"
            value={formData.slots}
            onChange={(e) => handleChange('slots', e.target.value)}
            onWheel={stopWheelNudge}
            fullWidth
            size="small"
                helperText="Number of available slots for sessions (can be customized per day)"
              />

              <Divider sx={{ my: 1 }} />

              {startDate && Object.keys(selectedDays).some(d => selectedDays[d]) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Scheduled Sessions:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {getDaysFromStartDate(startDate)
                      .filter(dayInfo => selectedDays[dayInfo.dateKey])
                      .map(dayInfo => (
                        <li key={dayInfo.dateKey}>
                          <Typography variant="body2">
                            <strong>{dayInfo.label}</strong>: {dayInfo.date.toLocaleDateString('en-US', { 
                              weekday: 'long',
                              month: 'long', 
                              day: 'numeric',
                              year: 'numeric'
                            })}
                            {dayTimes[dayInfo.dateKey] && ` at ${dayTimes[dayInfo.dateKey]} IST`}
                            {` - Slots: ${daySlots[dayInfo.dateKey] || formData.slots}`}
                          </Typography>
                        </li>
                      ))}
                  </Box>
                </Alert>
              )}

              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Select Days & Set Times *
              </Typography>

              <Box display="flex" flexDirection="column" gap={2}>
                {startDate ? (
                  getDaysFromStartDate(startDate).map((dayInfo) => (
                    <Box key={dayInfo.dateKey}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedDays[dayInfo.dateKey] || false}
                              onChange={() => handleDayToggle(dayInfo.dateKey)}
                            />
                          }
                          label={dayInfo.label}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({dayInfo.date.toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })})
                        </Typography>
                      </Box>
                      {selectedDays[dayInfo.dateKey] && (
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <TextField
                            label={`Time for ${dayInfo.label} * (IST)`}
                            type="time"
                            value={dayTimes[dayInfo.dateKey] || ''}
                            onChange={(e) => handleDayTimeChange(dayInfo.dateKey, e.target.value)}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            helperText={`Scheduled for ${dayInfo.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${dayTimes[dayInfo.dateKey] || '--:--'} IST`}
                            inputProps={{
                              step: 60,
                            }}
                          />
                          <TextField
                            label={`Slots for ${dayInfo.label}`}
                            type="number"
                            value={daySlots[dayInfo.dateKey] ?? formData.slots}
                            onChange={(e) => handleDaySlotsChange(dayInfo.dateKey, e.target.value)}
                            onWheel={stopWheelNudge}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            helperText={daySlots[dayInfo.dateKey] ? `Custom slots: ${daySlots[dayInfo.dateKey]}` : `Using default slots: ${formData.slots || 'Not set'}`}
                          />
                        </Box>
                      )}
                    </Box>
                  ))
                ) : (
                  <Alert severity="warning">
                    Please select a start date to see available days
                  </Alert>
                )}
              </Box>
            </>
          )}

          {/* Switches */}
          <Box display="flex" flexDirection="column" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isLive}
                  onChange={(e) => handleChange('isLive', e.target.checked)}
                />
              }
              label="Session Live"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.onlyForFemales}
                  onChange={(e) => handleChange('onlyForFemales', e.target.checked)}
                />
              }
              label="Only for Females"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isTrialEnabled}
                  onChange={(e) => handleChange('isTrialEnabled', e.target.checked)}
                />
              }
              label="Mark as Trial Session"
            />
            {formData.isTrialEnabled && (
              <TextField
                label="Trial Price (₹)"
                type="number"
                value={formData.trialPrice}
                onChange={(e) => handleChange('trialPrice', e.target.value)}
                onWheel={stopWheelNudge}
                fullWidth
                size="small"
                helperText="Price for trial booking. Set 0 for free trial."
              />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading || 
            !formData.class || 
            !formData.slots ||
            (isEditMode ? !formData.date : (!startDate || Object.keys(selectedDays).filter(d => selectedDays[d]).length === 0 || Object.keys(selectedDays).some(d => selectedDays[d] && !dayTimes[d])))
          }
          startIcon={loading && <CircularProgress size={16} />}
        >
          {isEditMode ? 'Update' : `Create ${Object.keys(selectedDays).filter(d => selectedDays[d]).length || 0} Session${Object.keys(selectedDays).filter(d => selectedDays[d]).length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>

    <DuplicateSessionConfirmDialog
      open={!!dupInfo}
      info={dupInfo}
      loading={dupLoading}
      totalRequested={pendingSessions?.length}
      onCancel={() => { setDupInfo(null); setPendingSessions(null); }}
      onConfirm={handleConfirmDuplicate}
    />
    </>
  );
};

export default SessionFormDialog;

